import React, { useState, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAdminStore } from '../../stores/useAdminStore';
import { useUIStore } from '../../stores/useUIStore';
import { useSeasonsStore } from '../../stores/useSeasonsStore';
import { PhotoReorderButtons } from './PhotoReorderButtons';
import Button from '../ui/Button/Button';
import { BaseModal } from '../shared/Modal/BaseModal';
import { DeleteConfirmation } from './DeleteConfirmation';
import './AdminFormControl.css';
import './PhotoActionsModal.css';

export const PhotoActionsModal: React.FC = () => {
  const {
    showPhotoActionsModal,
    selectedPhotoForActions,
    selectedPhotoIndex,
    selectedPhotoTotalCount,
    isEditingCaption,
    closePhotoActionsModal,
    startCaptionEdit,
    stopCaptionEdit
  } = useAdminStore(
    useShallow(s => ({
      showPhotoActionsModal: s.showPhotoActionsModal,
      selectedPhotoForActions: s.selectedPhotoForActions,
      selectedPhotoIndex: s.selectedPhotoIndex,
      selectedPhotoTotalCount: s.selectedPhotoTotalCount,
      isEditingCaption: s.isEditingCaption,
      closePhotoActionsModal: s.closePhotoActionsModal,
      startCaptionEdit: s.startCaptionEdit,
      stopCaptionEdit: s.stopCaptionEdit,
    }))
  );

  const { showToast } = useUIStore();
  const { deletePhoto, updatePhoto, isLoading } = useSeasonsStore();

  const savedCaption = selectedPhotoForActions?.caption || '';
  const [captionValue, setCaptionValue] = useState(savedCaption);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Custom close handler that respects editing state
  const handleClose = () => {
    if (isEditingCaption) {
      stopCaptionEdit();
      setCaptionValue(savedCaption);
    } else {
      closePhotoActionsModal();
    }
  };

  React.useEffect(() => {
    setCaptionValue(selectedPhotoForActions?.caption || '');
    setShowDeleteConfirmation(false);
  }, [selectedPhotoForActions]);

  React.useEffect(() => {
    if (isEditingCaption && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isEditingCaption]);

  if (!showPhotoActionsModal || !selectedPhotoForActions) {
    return null;
  }

  const handleDelete = async () => {
    try {
      await deletePhoto(selectedPhotoForActions.id);
      showToast('Photo deleted successfully', 'success');
      closePhotoActionsModal();
    } catch (error) {
      showToast('Failed to delete photo', 'error');
    }
  };

  const handleCaptionSave = async () => {
    try {
      await updatePhoto(selectedPhotoForActions.id, { caption: captionValue });
      showToast('Caption updated successfully', 'success');
      stopCaptionEdit();
    } catch (error) {
      showToast('Failed to update caption', 'error');
    }
  };

  const handleCaptionCancel = () => {
    setCaptionValue(savedCaption);
    stopCaptionEdit();
  };

  return (
    <BaseModal
      isOpen={showPhotoActionsModal}
      onClose={handleClose}
      title="Photo Actions"
      className="photo-actions-modal"
      initialFocusRef={isEditingCaption ? textareaRef : firstButtonRef}
      data-testid="photo-actions-modal"
    >
      {!showDeleteConfirmation ? (
        <>
          {/* Photo Info */}
          <div className="photo-info">
            <p><strong>Order:</strong> #{selectedPhotoForActions.displayOrder}</p>
            <p><strong>Type:</strong> {selectedPhotoForActions.type}</p>
            <p><strong>Audience:</strong> {selectedPhotoForActions.audience === 'FAMILY' ? 'Family' : 'Personal'}</p>
          </div>

          {/* Caption Section */}
          <div className="caption-section">
            {isEditingCaption ? (
              <div className="caption-edit-form">
                <label htmlFor="caption-input">Caption:</label>
                <textarea
                  ref={textareaRef}
                  id="caption-input"
                  value={captionValue}
                  onChange={(e) => setCaptionValue(e.target.value)}
                  placeholder="Enter caption..."
                  rows={3}
                  className="admin-form-control caption-textarea"
                />
                <div className="caption-actions">
                  <Button size="action"
                    onClick={handleCaptionSave}
                    disabled={isLoading}
                    variant="primary"
                  >
                    {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button size="action"
                    onClick={handleCaptionCancel}
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="caption-display">
                <p><strong>Caption:</strong> {selectedPhotoForActions.caption || 'No caption'}</p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="actions-section">
            {!isEditingCaption && (
              <>
                <Button size="action"
                  ref={firstButtonRef}
                  onClick={startCaptionEdit}
                  variant="primary"
                  data-testid="edit-photo-button"
                >
                  ✏️ Edit Caption
                </Button>

                <Button size="action"
                  onClick={() => setShowDeleteConfirmation(true)}
                  disabled={isLoading}
                  variant="danger-outline"
                  data-testid="delete-photo-button"
                >
                  🗑️ Delete Photo
                </Button>
              </>
            )}
          </div>

          {/* Reorder Section */}
          {!isEditingCaption && (
            <div className="reorder-section">
              <h4>Reorder Photo</h4>
              <PhotoReorderButtons
                photo={selectedPhotoForActions}
                photoIndex={selectedPhotoIndex}
                totalPhotos={selectedPhotoTotalCount}
              />
            </div>
          )}
        </>
      ) : (
        <DeleteConfirmation
          title="Delete Photo?"
          prompt="Are you sure you want to delete this photo?"
          warning="This action cannot be undone."
          confirmLabel="Yes, Delete Photo"
          isDeleting={isLoading}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          showWarningIcon
          confirmTestId="confirm-delete-photo-button"
        />
      )}
    </BaseModal>
  );
};
